import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface BusinessSettings {
  business_id: string;
  tax_rate: number;
  late_fee_mode: 'flat' | 'percent' | 'off' | string;
  late_fee_amount: number;
  currency: string;
  updated_at: string;
}

interface ValidationResult {
  isValid: boolean;
  missingCriticalFields: string[];
  missingWarningFields: string[];
  businessSettings: BusinessSettings | null;
  isLoading: boolean;
  error: string | null;
}

export function useBusinessSetupValidation() {
  const { currentBusiness, token, isCookieMode, API_BASE_URL } = useAuth();
  const [validationResult, setValidationResult] = useState<ValidationResult>({
    isValid: false,
    missingCriticalFields: [],
    missingWarningFields: [],
    businessSettings: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!currentBusiness || !token) {
      setValidationResult(prev => ({ ...prev, isLoading: false }));
      return;
    }

    validateBusinessSetup();
  }, [currentBusiness, token]);

  const validateBusinessSetup = async () => {
    if (!currentBusiness || !token) return;

    try {
      setValidationResult(prev => ({ ...prev, isLoading: true, error: null }));

      console.log('[BusinessSetupValidation] Starting validation for business:', currentBusiness.id);

      // Fetch business settings
      const headers: any = {};
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const settingsResponse = await axios.get(`${API_BASE_URL}/businesses/${currentBusiness.id}/settings`, {
        headers,
        withCredentials: isCookieMode,
      });

      const businessSettings: BusinessSettings = settingsResponse.data;
      console.log('[BusinessSetupValidation] Fetched business settings:', businessSettings);

      // Check critical business fields
      const criticalFields = ['name', 'street', 'city', 'state', 'zip', 'phone'];
      const missingCriticalFields: string[] = [];

      criticalFields.forEach(field => {
        const value = currentBusiness[field as keyof typeof currentBusiness];
        console.log(`[BusinessSetupValidation] Checking field '${field}':`, value);
        if (!value) {
          missingCriticalFields.push(field);
        }
      });

      // Check critical settings fields
      console.log('[BusinessSetupValidation] Checking tax_rate:', businessSettings.tax_rate);
      if (!businessSettings.tax_rate || businessSettings.tax_rate <= 0) {
        missingCriticalFields.push('tax_rate');
      }

      // Check warning fields (email)
      const missingWarningFields: string[] = [];
      if (!currentBusiness.email) {
        missingWarningFields.push('email');
      }

      const isValid = missingCriticalFields.length === 0;

      console.log('[BusinessSetupValidation] Validation results:', {
        missingCriticalFields,
        missingWarningFields,
        isValid,
        businessSettings
      });

      setValidationResult({
        isValid,
        missingCriticalFields,
        missingWarningFields,
        businessSettings,
        isLoading: false,
        error: null,
      });

    } catch (error: any) {
      console.error('[BusinessSetupValidation] Error during validation:', error);
      setValidationResult({
        isValid: false,
        missingCriticalFields: [],
        missingWarningFields: [],
        businessSettings: null,
        isLoading: false,
        error: error.response?.data?.error || 'Failed to validate business setup',
      });
    }
  };

  const refreshValidation = () => {
    validateBusinessSetup();
  };

  return {
    ...validationResult,
    refreshValidation,
  };
} 